// LegalPage — renders any of the legal documents from data/legalDocs.js.
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, ChevronRight, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LEGAL_DOCS } from "@/data/legalDocs";

const ALL = Object.values(LEGAL_DOCS);

export default function LegalPage() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const doc = LEGAL_DOCS[slug];
  if (!doc) return <Navigate to="/legal/terms" replace />;
  return (
    <div data-testid={`legal-${slug}`} className="relative overflow-x-hidden">
      <Navbar />

      <section className="relative pt-24 lg:pt-32 pb-10 lg:pb-14">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="pill-mono inline-flex mb-5">
            <FileText size={11} className="mr-1.5 -ml-1" /> {t("pages.legal.pill")}
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }} className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.025em] leading-[1.05] text-text-1">
            {doc.title}
          </motion.h1>
          <p className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3 mt-4">{doc.updated}</p>
          {doc.intro && <p className="text-text-2 mt-6 leading-relaxed">{doc.intro}</p>}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 lg:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24">
              <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3 mb-3">{t("pages.legal.sections")}</div>
              <ul className="space-y-1.5">
                {doc.sections.map((s, i) => (
                  <li key={s.h}><a href={`#sec-${i + 1}`} className="block text-[13px] text-text-2 hover:text-text-1 transition-colors py-1 leading-snug">{s.h}</a></li>
                ))}
              </ul>
              <div className="mt-8">
                <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3 mb-3">{t("pages.legal.otherDocs")}</div>
                <ul className="space-y-1.5">
                  {ALL.filter((d) => d.slug !== doc.slug).map((d) => (
                    <li key={d.slug}><Link to={`/legal/${d.slug}`} className="inline-flex items-center gap-1 text-[13px] text-text-2 hover:text-text-1 transition-colors"><ChevronRight size={12} /> {d.title}</Link></li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>

          <article className="lg:col-span-9 space-y-10">
            {doc.sections.map((s, i) => (
              <motion.section
                key={s.h}
                id={`sec-${i + 1}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.5, delay: i * 0.02 }}
                className="scroll-mt-24"
              >
                <h2 className="font-display text-xl sm:text-2xl font-medium text-text-1 mb-4 leading-tight">{s.h}</h2>
                <div className="space-y-4 text-text-2 text-[14.5px] leading-relaxed">
                  {s.body.map((p, j) => <p key={j}>{p}</p>)}
                </div>
              </motion.section>
            ))}
            <div className="pt-6 border-t" style={{ borderColor: "var(--line)" }}>
              <Link to="/" className="inline-flex items-center gap-2 text-[13px] text-text-2 hover:text-text-1">
                <ArrowLeft size={14} /> {t("pages.legal.backHome")}
              </Link>
            </div>
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
}
